import React from 'react';
import {
    Box,
    ColumnLayout,
    Container,
    ExpandableSection,
    Grid,
    Header,
    Icon,
    Popover,
    Table,
} from '@amzn/awsui-components-react';
import TPPieChart from '../../shared/TPPieChart';
import { getReasonCodeDescription } from './util';

type ReportSummarySectionProps = {
    title: string;
    description: string;
    totalLabel: string;
    totalCount: number;
    successCount: number;
    failureCount: number;
    pendingCount: number;
    reasonCodeSummary: Record<string, number>;
    failedItems: { id: string; reasonCode: string }[];
    failedItemIdHeader: string;
    failedItemsLabel: string;
    ariaDescription: string;
    ariaLabel: string;
    emptyDataDescription: string;
    noMatchDataDescription: string;
};

export default function ReportSummarySection(props: ReportSummarySectionProps) {
    const calcPercentage = (count: number) =>
        props.totalCount ? (count / props.totalCount) * 100 : 0;

    const pieChartData = [
        { title: 'Success', percentage: calcPercentage(props.successCount), value: props.successCount },
        { title: 'Failed', percentage: calcPercentage(props.failureCount), value: props.failureCount },
        { title: 'Pending', percentage: calcPercentage(props.pendingCount), value: props.pendingCount },
    ].filter(slice => slice.value > 0);

    const reasonCodeEntries = Object.entries(props.reasonCodeSummary);

    return (
        <Grid gridDefinition={[{ colspan: { default: 7, xl: 8 } }, { colspan: { default: 5, xl: 4 } }]}>
            <Container header={
                <Header variant="h2" description={props.description}>
                    {props.title}
                </Header>}>
                <ColumnLayout columns={5} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Success</Box>
                        <Box variant="h1">{props.successCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Failed</Box>
                        <Box variant="h1">{props.failureCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Pending</Box>
                        <Box variant="h1">{props.pendingCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">{props.totalLabel}</Box>
                        <Box variant="h1">{props.totalCount}</Box>
                    </div>
                    <div>
                        <Box variant="awsui-key-label">Success Rate</Box>
                        <Box variant="h1">{calcPercentage(props.successCount).toFixed(1)}%</Box>
                    </div>
                </ColumnLayout>

                {props.failedItems.length > 0 && (
                    <ExpandableSection headerText={`${props.failedItemsLabel} (${props.failedItems.length})`} variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'id', header: props.failedItemIdHeader, cell: (item) => item.id },
                                { id: 'reasonCode', header: 'Reason Code', cell: (item) => (
                                    <Popover header={item.reasonCode} content={getReasonCodeDescription(item.reasonCode)}>
                                        <Box color="text-status-info" fontSize="body-s">{item.reasonCode} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                            ]}
                            items={props.failedItems}
                            variant="embedded"
                        />
                    </ExpandableSection>
                )}

                {reasonCodeEntries.length > 0 && (
                    <ExpandableSection headerText="Failure Breakdown by Reason Code" variant="footer">
                        <Table
                            columnDefinitions={[
                                { id: 'reasonCode', header: 'Reason Code', cell: (item: [string, number]) => (
                                    <Popover header={item[0]} content={getReasonCodeDescription(item[0])}>
                                        <Box color="text-status-info" fontSize="body-s">{item[0]} <Icon name="status-info" size="small" /></Box>
                                    </Popover>
                                )},
                                { id: 'count', header: 'Count', cell: (item: [string, number]) => item[1] },
                            ]}
                            items={reasonCodeEntries}
                            variant="embedded"
                        />
                    </ExpandableSection>
                )}
            </Container>
            <TPPieChart
                data={pieChartData}
                totalUnits={props.totalCount}
                ariaDescription={props.ariaDescription}
                ariaLabel={props.ariaLabel}
                emptyDataDescription={props.emptyDataDescription}
                noMatchDataDescription={props.noMatchDataDescription}
            />
        </Grid>
    );
}
